import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import { PageShell } from '@components/common/PageShell'
import { DataTable, DataTableColumn } from '@components/common/DataTable'
import type { CrashGroup } from '@api/types/crashReports'
import { useCrashGroups } from '../hooks/useCrashReports'
import { CrashKindChip } from '../components/CrashKindChip'
import { CRASH_WINDOWS, formatDateTime, formatPlatform, formatRelative, formatVersion } from '../components/crashFormat'

/**
 * Crash groups reported by the mobile app.
 *
 * The window and platform live in the URL so the back button from a group returns to the same
 * slice, and a link to "Android crashes this week" can be pasted into a chat.
 */
export default function CrashesPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const days = Number(searchParams.get('days')) || 30
  const platform = searchParams.get('platform') ?? ''

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const groups = useCrashGroups({ days, platform: platform || undefined })

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
    setPage(0)
  }

  const columns = useMemo<DataTableColumn<CrashGroup>[]>(
    () => [
      {
        key: 'message',
        header: 'Crash',
        primary: true,
        render: (g) => (
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <CrashKindChip kind={g.kind} />
            {/* Clamped to three lines rather than one: on a phone card the first line of a crash
                message is usually just the error class, and the useful part was only in a tooltip. */}
            <Typography
              variant="body2"
              title={g.message}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
                maxWidth: { xs: '100%', sm: 420, lg: 640 },
              }}
            >
              {g.message || '(no message)'}
            </Typography>
          </Stack>
        ),
      },
      {
        key: 'count',
        header: 'Count',
        align: 'right',
        render: (g) => (
          <Typography variant="body2" fontWeight="bold">
            {g.count.toLocaleString()}
          </Typography>
        ),
      },
      {
        key: 'users',
        header: 'Users',
        align: 'right',
        render: (g) => <Typography variant="body2">{g.affectedUsers.toLocaleString()}</Typography>,
      },
      {
        key: 'lastSeen',
        header: 'Last seen',
        render: (g) => (
          <Typography variant="body2" title={formatDateTime(g.lastSeen)} sx={{ whiteSpace: 'nowrap' }}>
            {formatRelative(g.lastSeen)}
          </Typography>
        ),
      },
      {
        key: 'build',
        header: 'Latest build',
        hideBelow: 'md',
        render: (g) => (
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {formatVersion(g.latestAppVersion, g.latestBuildNumber)}
          </Typography>
        ),
      },
      {
        key: 'platforms',
        header: 'Platforms',
        hideBelow: 'md',
        render: (g) => (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {g.platforms.length ? g.platforms.map(formatPlatform).join(', ') : '—'}
          </Typography>
        ),
      },
    ],
    [],
  )

  const rows = groups.data ?? []
  const totalReports = rows.reduce((sum, g) => sum + g.count, 0)

  return (
    <PageShell
      title="Crashes"
      subtitle={
        groups.data
          ? `${rows.length.toLocaleString()} crash group${rows.length === 1 ? '' : 's'}, ${totalReports.toLocaleString()} report${totalReports === 1 ? '' : 's'} in the last ${days} days`
          : `Mobile app crashes over the last ${days} days`
      }
      actions={
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: 120, flex: { xs: 1, sm: 'none' } }}>
            <InputLabel id="crash-window">Window</InputLabel>
            <Select
              labelId="crash-window"
              label="Window"
              value={days}
              onChange={(e) => setParam('days', String(e.target.value))}
            >
              {CRASH_WINDOWS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d} days
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130, flex: { xs: 1, sm: 'none' } }}>
            <InputLabel id="crash-platform">Platform</InputLabel>
            <Select
              labelId="crash-platform"
              label="Platform"
              value={platform}
              onChange={(e) => setParam('platform', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="android">Android</MenuItem>
              <MenuItem value="ios">iOS</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      }
    >
      <DataTable
        rows={rows}
        columns={columns}
        getRowKey={(g) => g.fingerprint}
        loading={groups.isLoading}
        error={groups.isError ? 'Could not load crash reports.' : null}
        onRetry={() => groups.refetch()}
        emptyMessage="No crashes reported in this period."
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(g) =>
          navigate(`/crashes/${encodeURIComponent(g.fingerprint)}?days=${days}`, { state: { group: g } })
        }
      />
    </PageShell>
  )
}
